import { useMemo, useEffect } from 'react';
import { createFileRoute, notFound } from '@tanstack/react-router';
import { useSetAtom } from 'jotai';
import { DashboardPage } from '~/components/ui/dashboard/DashboardPage';
import { canAccess } from '~/utils/rbac';
import { useEnsureDashboard } from '~/hooks/useEnsureDashboard';
import { portfolioIdAtom } from '~/atoms/dashboardAtom';
import { ErrorComponent } from '~/routes/__root';
import type { Config } from '~/api/def/endpoints/dashboardEndpoint';
import { useRequireFeatureFlag } from '~/hooks/useRequireFeatureFlag';
import { FEATURE_FLAGS } from '~/config/orgConfigConstants';

// Define which widgets are allowed on the overview page
const ALLOWED_OVERVIEW_WIDGETS = [
  'proposed-portfolio',
  'historical-returns',
  'forward-returns',
  'sector-allocation',
  'risk-alerts',
  'expected-impact',
] as const;

/**
 * Default layout positions for overview dashboard
 */
const OVERVIEW_LAYOUT = [
  { h: 10, w: 4, x: 0, y: 0 }, // proposed-portfolio - Row 1 left
  { h: 10, w: 8, x: 4, y: 0 }, // risk-alerts - Row 1 right
  { h: 10, w: 4, x: 0, y: 10 }, // historical-returns - Row 2 left (below proposed)
  { h: 10, w: 8, x: 4, y: 10 }, // forward-returns - Row 2 right
  { h: 16, w: 12, x: 0, y: 20 }, // sector-allocation - Row 3 full width
];

const OVERVIEW_WIDGET_IDS = [
  'proposed-portfolio',
  'risk-alerts',
  'historical-returns',
  'forward-returns',
  'sector-allocation',
];

/**
 * Creates widget configs for overview dashboard with default layout
 * @param withUUID - If true, generates unique IDs with UUIDs; if false, uses simple widget IDs for reset matching
 */
const createOverviewDashboardConfig = (withUUID = true): Config[] => {
  return OVERVIEW_LAYOUT.map((position, index) => ({
    ...position,
    id: withUUID
      ? `${OVERVIEW_WIDGET_IDS[index]}-widget-${crypto.randomUUID()}`
      : OVERVIEW_WIDGET_IDS[index],
  }));
};

function OverviewComponent() {
  const { portfolioId } = Route.useParams();
  const setPortfolioId = useSetAtom(portfolioIdAtom);

  // Update portfolioId atom when route changes
  useEffect(() => {
    if (portfolioId) {
      setPortfolioId(portfolioId);
    }
  }, [portfolioId, setPortfolioId]);

  const initialOverviewConfig = useMemo(
    () => createOverviewDashboardConfig(true),
    [],
  );

  const overviewDefaultLayout = useMemo(
    () => createOverviewDashboardConfig(false),
    [],
  );

  const { dashboardId, error } = useEnsureDashboard({
    type: 'overview',
    name: 'Overview Dashboard',
    initialConfig: initialOverviewConfig,
    portfolioId,
  });

  if (error) {
    return <ErrorComponent />;
  }

  return (
    <DashboardPage
      dashboardId={dashboardId || ''}
      breadcrumbLabel="Overview"
      breadcrumbUrl={`/portfolio/${portfolioId}/overview`}
      pageTaglinePrefix="Overview"
      allowedWidgets={ALLOWED_OVERVIEW_WIDGETS}
      initialLayout={overviewDefaultLayout}
      emptyStateTitle="No Widgets Found"
      emptyStateMessage="This dashboard doesn't have any widgets yet. Add some widgets to get started."
      emptyStateButtonLabel="Add Widget"
    />
  );
}

const PortfolioOverviewRoute = () => {
  const { allowed } = useRequireFeatureFlag(FEATURE_FLAGS.PORTFOLIO_MANAGEMENT);
  if (!allowed) return null;
  return <OverviewComponent />;
};

export const Route = createFileRoute(
  '/_authLayout/portfolio/$portfolioId/overview',
)({
  beforeLoad: async () => {
    if (!canAccess('dashboard')) {
      throw notFound();
    }
  },
  component: PortfolioOverviewRoute,
});
